import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import SpeechRecognition, {
  useSpeechRecognition
} from "react-speech-recognition";
import { useAuth } from "@/features/auth/context/AuthContext";
import { sendMessageToGemini, getStoredHistory } from "../lib/gemini-exam";
import { AudioQueue } from "../lib/audio-engine";
import { MessageBubble } from "../components/MessageBubble";
import { AudioControls } from "../components/AudioControls";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, GraduationCap, AlertCircle, Mic } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function ExamSimulationPage() {
  const { t } = useTranslation("talent");
  const { profile } = useAuth();
  const { toast } = useToast();

  const [messages, setMessages] = useState<any[]>([]);
  const [inputBase, setInputBase] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);

  const audioQueueRef = useRef<AudioQueue | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();

  const currentInput = inputBase + (transcript || "");

  // Initialize AudioQueue
  useEffect(() => {
    // Load initial history
    const history = getStoredHistory(profile.id);
    if (history) {
      // We could display progress here if needed
    }

    audioQueueRef.current = new AudioQueue({
      onStart: () => setIsAiSpeaking(true),
      onEnd: () => setIsAiSpeaking(false)
    });
    return () => audioQueueRef.current?.stop();
  }, [profile.id]);

  // Scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Manage mic state
  useEffect(() => {
    if (!isAiSpeaking && listening) {
      // SpeechRecognition.startListening is usually triggered by user
    } else if (isAiSpeaking) {
      SpeechRecognition.stopListening();
    }
  }, [isAiSpeaking, listening]);

  const handleToggleListening = () => {
    console.log("Toggle Listening - Current state:", {
      listening,
      isMicrophoneAvailable,
      browserSupportsSpeechRecognition
    });

    if (listening) {
      console.log("Stopping listening manually");
      SpeechRecognition.stopListening();
    } else {
      resetTranscript();
      setInputBase("");

      try {
        SpeechRecognition.startListening({
          continuous: true,
          language: "de-DE"
        });
      } catch (err: any) {
        console.error("SpeechRecognition.startListening error:", err);
        toast({
          title: "Error starting microphone",
          description: err.message,
          variant: "destructive"
        });
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputBase(e.target.value);
    resetTranscript();
  };

  const handleSend = async () => {
    const textToSend = currentInput.trim();
    if (!textToSend || isProcessing) return;

    audioQueueRef.current?.stop();
    SpeechRecognition.stopListening();

    const userMessage = { text: textToSend, sender: "user" };
    setMessages((prev) => [...prev, userMessage]);

    setInputBase("");
    resetTranscript();
    setIsProcessing(true);

    try {
      const response = await sendMessageToGemini(
        profile.id,
        messages,
        textToSend
      );
      const aiMessage = { text: response.message, sender: "ai" };

      setMessages((prev) => [...prev, aiMessage]);

      if (response.message.german) {
        audioQueueRef.current?.add(response.message.german);
      }
    } catch (error: any) {
      console.error("Gemini Error:", error);
      toast({
        title: t("simulation_error_toast_title"),
        description: error.message || t("simulation_error_toast_description"),
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReplay = (text: string) => {
    audioQueueRef.current?.stop();
    audioQueueRef.current?.add(text);
  };

  if (!browserSupportsSpeechRecognition) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6 bg-white/50 rounded-3xl border border-dashed border-gray-200">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">
          {t("simulation_error_not_supported_title")}
        </h2>
        <p className="text-muted-foreground">
          {t("simulation_error_not_supported_subtitle")}
        </p>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto h-[calc(100dvh-80px)] md:h-[calc(100vh-120px)] flex flex-col pt-2 md:pt-4">
      <div className="flex items-center justify-between mb-4 md:mb-6 px-2 md:px-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 flex items-center gap-2 md:gap-3">
            <div className="bg-[#d769b3]/10 p-1.5 md:p-2 rounded-xl">
              <GraduationCap className="h-6 w-6 md:h-8 md:w-8 text-[#d769b3]" />
            </div>
            {t("nav_exam_simulation")}
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-0.5 md:mt-1 font-medium italic opacity-80">
            {t("simulation_subheader")}
          </p>
        </div>
      </div>

      <Card className="flex-1 flex flex-col overflow-hidden bg-gray-50/50 border-none shadow-none rounded-t-3xl md:rounded-3xl">
        <ScrollArea className="flex-1 p-3 md:p-6">
          <div className="flex flex-col space-y-2">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 md:py-20 text-center opacity-60">
                <div className="bg-white p-4 md:p-5 rounded-full shadow-sm mb-3 md:mb-4 ring-8 ring-[#d769b3]/5">
                  <Mic className="h-8 w-8 md:h-10 md:w-10 text-[#d769b3]" />
                </div>
                <p className="text-base md:text-lg font-medium text-gray-600 px-4">
                  {t("simulation_empty_title")}
                </p>
                <p className="text-xs md:text-sm text-gray-400 mt-1 max-w-[250px] md:max-w-xs px-4">
                  {t("simulation_empty_subtitle")}
                </p>
              </div>
            )}
            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} onReplay={handleReplay} />
            ))}
            {isProcessing && (
              <div className="flex items-center gap-2 md:gap-3 text-xs text-[#d769b3] font-medium animate-pulse ml-10 md:ml-12 py-2">
                <div className="h-1.5 w-1.5 md:h-2 md:w-2 bg-[#d769b3] rounded-full shadow-[0_0_8px_#d769b3]" />
                {t("simulation_processing")}
              </div>
            )}
            <div ref={bottomRef} className="h-4" />
          </div>
        </ScrollArea>

        <div className="p-2 md:p-4 bg-transparent space-y-3 md:space-y-4">
          <div className="flex gap-2 items-center bg-white p-1 rounded-2xl border shadow-sm ring-4 ring-gray-50/50 mx-1 md:mx-0">
            <Input
              value={currentInput}
              onChange={handleInputChange}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder={t("simulation_placeholder")}
              className="flex-1 h-10 md:h-12 border-none shadow-none focus-visible:ring-0 text-sm md:text-base text-gray-700 bg-transparent"
              disabled={isProcessing}
            />
            <Button
              onClick={handleSend}
              disabled={isProcessing || !currentInput.trim()}
              className="h-9 w-9 md:h-10 md:w-10 rounded-xl bg-[#d769b3] hover:bg-[#c558a1] shadow-md transition-all hover:scale-105 active:scale-95 shrink-0"
            >
              <Send className="h-3.5 w-3.5 md:h-4 md:w-4" />
            </Button>
          </div>

          <div className="flex justify-center pb-1 md:pb-2">
            <AudioControls
              isListening={listening}
              onToggleListening={handleToggleListening}
              disabled={isProcessing || isAiSpeaking}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
